import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { checkboxRequired } from '@app/form/validators/checkbox-required.validator';
import { Move } from '@app/move/interfaces/move';
import { MoveSandbox } from '@app/move/sandboxes/move.sandbox';
import { Navigation } from '@app/navigation/enums/navigation.enum';
import { AppNavigationSandbox } from '@app/navigation/sandboxes/navigation.sandbox';
import { RealEstateGroupSandbox } from '@app/real-estate-group/sandboxes/real-estate-group.sandbox';
import { AppUiSandbox } from '@app/ui/sandboxes/ui.sandbox';
import { RxjsComponent } from '@smooved/core';
import { ButtonAppearance, ButtonSize, UiContext } from '@smooved/ui';
import { isBoolean } from 'lodash';
import { Observable, of, zip } from 'rxjs';
import { exhaustMap, finalize, map, take } from 'rxjs/operators';
import { OtherServicesForm } from './other-services-form.enum';
import { serviceTelecom, realEstateAgentMoverProfileConsentOption, requestWithoutMoverOptions } from './other-services.constants';

@Component({
    selector: 'app-other-services-container',
    templateUrl: './other-services.container.html',
    styleUrls: ['./other-services.container.scss'],
})
export class OtherServicesContainer extends RxjsComponent implements OnInit {
    public readonly uiContext = UiContext;
    public readonly buttonSize = ButtonSize;
    public readonly buttonAppearance = ButtonAppearance;

    public otherServicesForm = OtherServicesForm;
    public form: UntypedFormGroup = this.formBuilder.group({
        [OtherServicesForm.OtherServicesTelecom]: [true],
        [OtherServicesForm.RequestWithoutMover]: [null, Validators.required],
        [OtherServicesForm.AdditionalNotes]: [null],
        [OtherServicesForm.RealEstateAgentConsent]: [false, checkboxRequired],
    });

    public realEstateAgentConsentOption = realEstateAgentMoverProfileConsentOption;
    public serviceTelecom = serviceTelecom;
    public requestWithoutMoverOptions = requestWithoutMoverOptions;

    constructor(
        public readonly realEstateGroupSandbox: RealEstateGroupSandbox,
        public readonly uiSandbox: AppUiSandbox,
        public readonly moveSandbox: MoveSandbox,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly navigationSandbox: AppNavigationSandbox,
    ) {
        super();
    }

    public ngOnInit(): void {
        zip(this.moveSandbox.additionalNotesOnce$, this.moveSandbox.interestedOnce$)
            .pipe(take(1))
            .subscribe(([additionalNotes, interested]) => {
                this.form.get(OtherServicesForm.AdditionalNotes).patchValue(additionalNotes);
                if (interested) {
                    if (isBoolean(interested?.telecom)) {
                        this.form.get(OtherServicesForm.OtherServicesTelecom).patchValue(interested?.telecom);
                    }
                }
            });
    }

    public goToPrevious(): void {
        this.buildPatch$()
            .pipe(take(1))
            .subscribe((patch) => {
                this.moveSandbox.patchProperty('', patch);
                this.router
                    .navigate(['../contract/payment-details'], {
                        relativeTo: this.route,
                    })
                    .then();
            });
    }

    public onSubmit(event): void {
        if (this.form.valid) {
            zip(this.moveSandbox.moveOnce$, this.buildPatch$().pipe(take(1)))
                .pipe(take(1), map(this.formatPayload), exhaustMap(this.handleSubmit))
                .subscribe(this.handleSubmitSuccess);
        }
    }

    private buildPatch$(): Observable<Move> {
        const telecom = !!this.form.get(OtherServicesForm.OtherServicesTelecom).value;

        const patch: Move = {
            interested: {
                telecom,
            },
            legal: {
                realEstateAgent: this.form.get(OtherServicesForm.RealEstateAgentConsent).value,
            },
            requestWithoutMover: this.form.get(OtherServicesForm.RequestWithoutMover).value,
        };

        const additionalNotes: string = this.form.get(OtherServicesForm.AdditionalNotes).value;
        if (additionalNotes) {
            patch.additionalNotes = additionalNotes;
        }

        return of(patch);
    }

    private formatPayload = ([move, patch]): Move => {
        return {
            ...move,
            ...patch,
        };
    };

    private handleSubmit = (move: Move): Observable<{ payload: Move; moveId: string }> => {
        this.uiSandbox.showLoadingOverlay();
        const payload = move;
        const submit$ = move._id ? this.moveSandbox.updateMoveForTransferee(payload) : this.moveSandbox.createMoveForTransferee(payload);
        return submit$.pipe(
            map((moveId) => ({
                payload,
                moveId,
            })),
            finalize(() => this.uiSandbox.hideLoadingOverlay()),
        );
    };

    private handleSubmitSuccess = (result): void => {
        if (!result) return;

        if (result.payload.user?.landAgent) {
            void this.router.navigate(['../../', Navigation.Moves, Navigation.ConfirmEnergy], {
                relativeTo: this.route,
                queryParams: { id: result.moveId },
            });
            return;
        }

        result.payload.interested?.telecom
            ? void this.navigationSandbox.goToTelecomInterest()
            : void this.navigationSandbox.goToOtsSuccess();
    };
}
